import { Link } from 'react-router-dom';

export default function Programs() {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const programs = [
    {
      icon: 'ri-book-open-line',
      title: 'Book Reading Class',
      age: 'Ages 6–14',
      color: '#153223',
      description: 'A structured reading program guiding students through quality storybooks and level-based vocabulary development.',
      features: [
        'Curated list of 18 storybooks and U.S.-based novel workbook',
        'Guided in-class reading',
        'Comprehension discussions',
        'Singapore-based grammar worksheets',
        'Mandatory Synonym-Building Program',
        'Strong focus on reading confidence and overall understanding'
      ],
      bestFor: 'Students who struggle with reading aloud, vocabulary weakness, or difficulty understanding texts.',
      cta: 'Explore Book Reading Class',
      ctaLink: '/book-reading-class',
      image: 'https://public.readdy.ai/ai/img_res/634eff28ec3bd529c50ca871e4f54c46.jpg'
    },
    {
      icon: 'ri-quill-pen-line',
      title: 'Focus Writing Class',
      age: 'Ages 11–15',
      color: '#2d5a3f',
      description: 'A skills-focused writing program combining content knowledge with structured writing techniques.',
      features: [
        'Narrative, descriptive, article, report, speech, letter & opinion writing',
        'National Geographic Reading Explorer',
        'Weekly 45-minute Writing Dictation',
        'Aligned with IGCSE-style writing practice'
      ],
      bestFor: 'Students preparing for advanced writing and future IGCSE-level expectations.',
      cta: 'Learn About Focus Writing Class',
      ctaLink: null,
      image: 'https://readdy.ai/api/search-image?query=Student%20aged%2011%20to%2014%20writing%20in%20notebook%20with%20National%20Geographic%20Reading%20Explorer%20textbook%20on%20wooden%20desk%20in%20home%20study%20environment%20focused%20learning%20warm%20natural%20lighting%20simple%20clean%20beige%20background%20educational%20writing%20practice%20authentic%20home%20setting%20professional%20photography%20high%20quality%20realistic&width=600&height=400&seq=program-writing-002&orientation=landscape'
    },
    {
      icon: 'ri-user-voice-line',
      title: 'English for Adults',
      age: 'Ages 18+',
      color: '#1a3d2b',
      description: 'A CEFR-aligned programme using the Keynote Series by National Geographic Learning — build real communication skills through global topics and interactive discussions.',
      features: [
        'CEFR-aligned learning framework',
        'Keynote Series by National Geographic Learning',
        'TED-style discussions on real-world topics',
        'Presentation practice, group discussions & pair work'
      ],
      bestFor: 'Adults looking to improve English communication and speaking confidence.',
      cta: 'Learn About English for Adults',
      ctaLink: '/english-for-adults',
      image: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20real%20adult%20students%20having%20engaging%20discussion%20in%20modern%20bright%20classroom%20sitting%20around%20table%20with%20National%20Geographic%20textbooks%20professional%20stock%20photography%20authentic%20people%20different%20ethnicities%20ages%2025%20to%2050%20natural%20expressions%20warm%20lighting%20collaborative%20learning%20environment%20high%20quality%20realistic%20photo&width=600&height=400&seq=program-adults-diverse-003&orientation=landscape'
    },
    {
      icon: 'ri-award-line',
      title: 'English Language Academic Proficiency Certification Test',
      age: 'Ages 10–16',
      color: '#3d6a4f',
      description: 'A CEFR-aligned English proficiency test credited by Password UK, designed for students who want to know their true academic English level.',
      features: [
        'International-standard CEFR-aligned test',
        'Credited and issued by Password UK',
        'Measures real academic English level',
        'One-off test (no classes required)',
        'Includes an official result report + certification',
        'May be used for school enrolment (depending on school requirements)'
      ],
      bestFor: '',
      cta: 'Learn More About the Certification Test',
      ctaLink: null,
      image: 'https://readdy.ai/api/search-image?query=Official%20English%20language%20certification%20test%20certificate%20with%20CEFR%20level%20marking%20professional%20academic%20credential%20document%20on%20wooden%20desk%20simple%20clean%20beige%20background%20educational%20achievement%20international%20standard%20high%20quality%20realistic%20photography&width=600&height=400&seq=program-cert-001&orientation=landscape',
      badge: 'Coming Soon'
    }
  ];

  return (
    <section id="programs" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="font-['Yeseva_One',serif] text-3xl md:text-5xl text-[#153223] mb-3 md:mb-4">
            Our Programs
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto px-4">
            Comprehensive English learning programs tailored for Malaysian and ESL students
          </p>
        </div>

        <div className="space-y-6 md:space-y-8">
          {programs.map((program, index) => (
            <div 
              key={index}
              className="bg-[#F5F1E8] rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 relative"
            >
              {program.badge && (
                <div className="absolute top-4 right-4 md:top-6 md:right-6 bg-[#FFE66D] text-[#153223] font-bold px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm z-10">
                  {program.badge}
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-0 md:gap-8">
                <div className="p-6 md:p-12 flex flex-col justify-center order-2 md:order-1">
                  <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                    <div 
                      className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ backgroundColor: program.color }}
                    >
                      <i className={`${program.icon} text-2xl md:text-3xl text-white`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl md:text-3xl font-bold text-[#153223]">
                        {program.title}
                      </h3>
                      <p className="text-[#FFE66D] font-semibold bg-[#153223] inline-block px-2 md:px-3 py-1 rounded-full text-xs md:text-sm mt-1">
                        {program.age}
                      </p>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-gray-700 mb-4 md:mb-6 leading-relaxed font-semibold">
                    {program.description}
                  </p>

                  <h4 className="text-base md:text-lg font-bold text-[#153223] mb-2 md:mb-3">Key Features:</h4>
                  <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                    {program.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 md:gap-3 text-sm md:text-base text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#FFE66D] text-lg md:text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {program.bestFor && (
                    <div className="bg-[#F5F1E8] p-3 md:p-4 rounded-lg mb-4 md:mb-6">
                      <p className="text-xs md:text-sm font-bold text-[#153223] mb-1">Best For:</p>
                      <p className="text-gray-700 text-xs md:text-sm leading-relaxed">{program.bestFor}</p>
                    </div>
                  )}

                  {program.ctaLink ? (
                    <Link
                      to={program.ctaLink}
                      onClick={handleScrollToTop}
                      className="whitespace-nowrap bg-[#153223] hover:bg-[#1f4433] text-white font-bold px-5 py-2.5 md:px-6 md:py-3 rounded-lg transition-all duration-300 cursor-pointer flex items-center gap-2 w-fit text-sm md:text-base"
                    >
                      <i className="ri-arrow-right-line"></i>
                      {program.cta}
                    </Link>
                  ) : program.badge ? (
                    <div className="bg-[#FFE66D]/20 border-2 border-[#FFE66D] px-4 py-2 md:px-6 md:py-3 rounded-lg flex items-center gap-2 md:gap-3 w-fit">
                      <i className="ri-notification-3-line text-lg md:text-xl text-[#153223]"></i>
                      <div>
                        <p className="font-bold text-[#153223] text-sm md:text-base">Stay Tuned!</p>
                        <p className="text-xs md:text-sm text-gray-600">More details coming soon</p>
                      </div>
                    </div>
                  ) : (
                    <Link
                      to="/focus-writing-class"
                      onClick={handleScrollToTop}
                      className="whitespace-nowrap bg-[#153223] hover:bg-[#1f4433] text-white font-bold px-5 py-2.5 md:px-6 md:py-3 rounded-lg transition-all duration-300 cursor-pointer flex items-center gap-2 w-fit text-sm md:text-base"
                    >
                      <i className="ri-arrow-right-line"></i>
                      {program.cta}
                    </Link>
                  )}
                </div>

                <div className="relative h-48 md:h-full md:min-h-[400px] order-1 md:order-2">
                  <img 
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
